/* eslint-disable no-console */
import type { LogEntry, LogLevel, Transport } from './logger.config';

import { LOG_LEVELS } from './logger.config';

/**
 * Configuration options for the console transport.
 * Defines settings for console-based logging.
 */
export type ConsoleTransportOptions = {
    /** Minimum log level for console output */
    level?: LogLevel;
};

/**
 * Console transport for logging.
 * Outputs log entries to the console with appropriate formatting and color coding.
 */
export class ConsoleTransport implements Transport {
    /** The minimum log level this transport will process */
    public level: LogLevel;

    /**
     * Creates a new console transport instance.
     * @param options Configuration options for the console transport
     */
    constructor(options: ConsoleTransportOptions = {}) {
        this.level = options.level || 'INFO';
    }

    /**
     * Processes and outputs a log entry to the console.
     * Uses appropriate console methods based on log level.
     * @param entry The log entry to process and output
     */
    public log(entry: LogEntry): void {
        const { level, messages, version } = entry;

        // Check if the log level is enabled for this transport
        if (entry.level < LOG_LEVELS[this.level]) {
            return; // If the log level is not enabled, do not log
        }

        const formattedMessages = this.formatMessages(messages);
        const timestamp = this.formatTimestamp(entry);

        // Output to console with appropriate method based on log level
        switch (level) {
            case LOG_LEVELS.DEBUG:
                console.log(`[${timestamp}] ${entry.loggerName} (${version}): ${formattedMessages}`);
                break;
            case LOG_LEVELS.INFO:
                console.info(`[${timestamp}] ${entry.loggerName} (${version}): ${formattedMessages}`);
                break;
            case LOG_LEVELS.WARN:
                console.warn(`[${timestamp}] ${entry.loggerName} (${version}): ${formattedMessages}`);
                break;
            case LOG_LEVELS.ERROR:
                console.error(`[${timestamp}] ${entry.loggerName} (${version}): ${formattedMessages}`);
                break;
            default:
                console.log(`[${timestamp}] ${entry.loggerName} (${version}): ${formattedMessages}`);
        }
    }

    /**
     * Formats log messages into a single string.
     * Handles different types of messages (strings, errors, objects).
     * @param messages The messages to format
     * @returns Formatted message string
     */
    private formatMessages(messages: unknown | unknown[]): string {
        try {
            return (Array.isArray(messages) ? messages : [messages]).map((msg) => {
                if (typeof msg === 'string') {
                    return msg;
                }
                if (msg instanceof Error) {
                    return msg.stack || msg.message; // Prefer stack trace if available
                }
                // Attempt to stringify objects/arrays, handle potential circular refs
                try {
                    return JSON.stringify(msg, null, 2);
                }
                // eslint-disable-next-line unused-imports/no-unused-vars
                catch (error) {
                    return '[Unserializable Object]';
                }
            }).join(' '); // Join multiple messages with a space
        }
        catch {
            console.error('Error formatting messages', messages);
            // Fallback in case of any error during formatting
            return '[Error formatting messages]';
        }
    }

    /**
     * Formats the timestamp of a log entry.
     * @param entry The log entry containing the timestamp
     * @returns Formatted timestamp string
     */
    private formatTimestamp(entry: LogEntry): string {
        return entry.timestamp.toLocaleTimeString();
    }
}
